import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import type { ImpersonationProfile, Me } from '@app/shared';
import {
  ApiError, getImpersonation, getImpersonationConfig, getLastTokenError, getMe,
  invalidateLoanList, reauthenticate, setImpersonation,
} from '../api';
import { clearAllViewState } from '../table/viewState';
import { loginRequest } from './msalConfig';

interface AuthContextValue {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Why the signed-in account cannot use the app, when that is the reason `me`
   * is null. Distinguishes "not signed in yet" from "signed in as an account the
   * API rejects" — the two look identical on screen otherwise, and the second is
   * what happens when the browser hands Entra the wrong Microsoft account.
   */
  authError: string | null;
  /** Sign in again from scratch, choosing a different account. */
  switchAccount: () => Promise<void>;
  hasRole: (role: string) => boolean;
  /**
   * Impersonation testing aid. `impersonationProfiles` is empty in production —
   * the API only advertises a roster when IMPERSONATION_ENABLED is set — so any
   * UI keyed off its length disappears on its own. See shared/src/index.ts.
   */
  impersonationProfiles: ImpersonationProfile[];
  impersonating: string | null;
  impersonate: (key: string | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [impersonationProfiles, setImpersonationProfiles] = useState<ImpersonationProfile[]>([]);
  const [impersonating, setImpersonatingState] = useState<string | null>(() => getImpersonation());

  const refresh = useCallback(async () => {
    // MSAL is mid-flight (handling the redirect back from Entra, or acquiring a
    // token). Calling /api/me now would fail for no reason other than timing and
    // paint the sign-in prompt over a sign-in that is actually succeeding.
    if (inProgress !== InteractionStatus.None) {
      setLoading(true);
      return;
    }
    if (!isAuthenticated) {
      setMe(null);
      setAuthError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setMe(await getMe());
      setAuthError(null);
    } catch (e) {
      setMe(null);
      const tokenError = getLastTokenError();
      if (tokenError) {
        setAuthError(`Could not get an access token for this account: ${tokenError}`);
      } else if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        const account = instance.getActiveAccount();
        setAuthError(
          `Signed in as ${account?.username ?? 'this account'}, but the API rejected it `
          + `(${e.status}: ${e.message}). If that is not your UMWSB account, sign in again `
          + 'and pick the right one.',
        );
      } else {
        setAuthError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, inProgress, instance]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Ask the API whether impersonation is available. A failure here (403, or the
  // route absent on an older API) is not an error worth surfacing — it just
  // means no picker.
  useEffect(() => {
    if (!isAuthenticated || inProgress !== InteractionStatus.None) {
      setImpersonationProfiles([]);
      return;
    }
    getImpersonationConfig()
      .then(cfg => setImpersonationProfiles(cfg.enabled ? cfg.profiles : []))
      .catch(() => setImpersonationProfiles([]));
  }, [isAuthenticated, inProgress]);

  // Clear a stale selection: if the stored key isn't in the roster the server
  // offers (feature switched off, or the roster changed), drop it rather than
  // sending a header that will now 400.
  useEffect(() => {
    if (!impersonating) return;
    if (impersonationProfiles.length === 0) return;
    if (impersonationProfiles.some(p => p.key === impersonating)) return;
    clearAllViewState();
    invalidateLoanList();
    setImpersonation(null);
    setImpersonatingState(null);
    void refresh();
  }, [impersonating, impersonationProfiles, refresh]);

  const impersonate = useCallback(async (key: string | null) => {
    // Borrowing another identity invalidates every remembered view: the default
    // assignment chip is built around an OID, so a view saved as one user filters
    // on a person the next user is not — an unsatisfiable filter that shows an
    // empty grid and explains nothing. Impersonation is stored in localStorage and
    // so survives a refresh, while views live in sessionStorage and survive it too,
    // which is exactly the pairing that would keep serving the wrong chip.
    clearAllViewState();
    // The cached list was fetched as somebody else. It is keyed only on
    // includeDeleted, which an admin may have had on and the borrowed identity may
    // not be allowed to see, so painting it after the switch would show rows this
    // user cannot ask for.
    invalidateLoanList();
    setImpersonation(key);
    setImpersonatingState(key);
    await refresh();
  }, [refresh]);

  const login = useCallback(async () => {
    // loginRedirect throws interaction_in_progress if MSAL is still settling a
    // previous redirect, which is easy to hit by clicking the button twice.
    if (inProgress !== InteractionStatus.None) return;
    setAuthError(null);
    await instance.loginRedirect(loginRequest);
  }, [instance, inProgress]);

  const switchAccount = useCallback(async () => {
    if (inProgress !== InteractionStatus.None) return;
    setAuthError(null);
    await reauthenticate();
  }, [inProgress]);

  const logout = useCallback(async () => {
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    await instance.logoutRedirect({ account });
  }, [instance]);

  const hasRole = useCallback((role: string) => !!me?.roles.includes(role), [me]);

  return (
    <AuthContext.Provider value={{
      me, loading, refresh, login, logout, authError, switchAccount, hasRole,
      impersonationProfiles, impersonating, impersonate,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
